/**
 * Sites → Plans tab content.
 *
 * One tabbed surface for safety/operating planning: the drill schedule plus the
 * whakahaere operating-model config (ADR-126) — operating calendar (recurring
 * hours, closures/exceptions, scheduled drills), per-tenant routing policy, and
 * the operating-terminology overlay. Each operating sub-tab renders a
 * site-grouped table (an "Organization default" row + one per site, expand to
 * view/edit) — no org→site scope selector. The drill schedule manages
 * its own site selection.
 *
 * The operating config nests here under Sites → Plans, driven by the
 * `?subtab=` query param.
 */

import { Box } from '@chakra-ui/react';
import { TabMenu, type TabItem } from '@themes/aegix';
import {
  HiClipboardDocumentList,
  HiCalendarDays,
  HiAdjustmentsHorizontal,
  HiLanguage,
} from 'react-icons/hi2';
import { useSearchParams } from 'react-router-dom';
import {
  OperatingCalendarEditor,
  RoutingPolicyEditor,
  TerminologyEditor,
} from '../whakahaere';
import { DrillScheduleList } from './DrillScheduleList';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';

export function SitesPlans() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const localizedSubTabs: TabItem[] = useMemo(() => [
    {
      value: 'drills',
      label: t('sites.subtabs.drills', 'Drill Schedule'),
      icon: HiClipboardDocumentList,
      'data-e2e': 'subtab-drills',
    },
    {
      value: 'hours',
      label: t('sites.subtabs.hours', 'Operating Hours'),
      icon: HiCalendarDays,
      'data-e2e': 'subtab-operating-hours',
    },
    {
      value: 'routing',
      label: t('sites.subtabs.routing', 'Routing'),
      icon: HiAdjustmentsHorizontal,
      'data-e2e': 'subtab-routing',
    },
    {
      value: 'terminology',
      label: t('sites.subtabs.terminology', 'Terminology'),
      icon: HiLanguage,
      'data-e2e': 'subtab-terminology',
    },
  ], [t]);

  const activeSubTab = searchParams.get('subtab') || 'drills';

  const handleSubTabChange = (tab: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('subtab', tab);
      next.delete('id');
      return next;
    });
  };

  return (
    <Box>
      <TabMenu
        tabs={localizedSubTabs}
        activeTab={activeSubTab}
        onChange={handleSubTabChange}
        e2eTitle="SitesPlansSubTabs"
      />

      {activeSubTab === 'drills' && <DrillScheduleList />}
      {activeSubTab === 'hours' && <OperatingCalendarEditor />}
      {activeSubTab === 'routing' && <RoutingPolicyEditor />}
      {activeSubTab === 'terminology' && <TerminologyEditor />}
    </Box>
  );
}
